/**
 * usePlatformBanner
 * -----------------
 * Manages the platform-wide maintenance / announcement banner state.
 *
 * Storage key: 'em_platform_banner'
 * Shape:
 * {
 *   platform_maintenance: boolean,
 *   bannerText:   string,
 *   bannerColor:  string,
 *   bannerTheme:  string,            // see BANNER_THEMES for valid IDs
 *   dismissible:  boolean,
 *   showIcon:     boolean,
 *   startAt:      string | null,
 *   endAt:        string | null,
 * }
 *
 * The hook broadcasts a CustomEvent 'platform_banner_updated' so every
 * mounted consumer (App, admin panel) stays in sync without a page reload.
 */

import { useState, useEffect, useCallback } from 'react';

export const BANNER_STORAGE_KEY = 'em_platform_banner';

export const BANNER_DEFAULTS = {
  platform_maintenance: false,
  bannerText:   'We are currently performing scheduled maintenance. Some features may be temporarily unavailable.',
  bannerColor:  '#1a2744',
  bannerTheme:  'warning',
  dismissible:  true,
  showIcon:     true,
  startAt:      null,
  endAt:        null,
};

/**
 * BANNER_THEMES
 * -------------
 * Each entry has:
 *   id          – unique string used as the stored value
 *   label       – human-readable name shown in the admin picker
 *   group       – category for grouping in the UI
 *   bg          – Tailwind bg class (used when rendering without inline style)
 *   text        – Tailwind text class
 *   icon        – emoji / symbol shown beside the message
 *   color       – default hex background colour for this theme
 *   defaultText – suggested banner copy pre-filled when the theme is selected
 */
export const BANNER_THEMES = [
  // ── System ────────────────────────────────────────────────────────────────
  {
    id:          'warning',
    label:       'Warning',
    group:       'System',
    bg:          'bg-amber-500',
    text:        'text-white',
    icon:        '⚠️',
    color:       '#f59e0b',
    defaultText: 'Please be aware of an important update regarding our platform.',
  },
  {
    id:          'info',
    label:       'Information',
    group:       'System',
    bg:          'bg-blue-600',
    text:        'text-white',
    icon:        'ℹ️',
    color:       '#2563eb',
    defaultText: 'Here is an important notice for all platform users.',
  },
  {
    id:          'danger',
    label:       'Danger / Urgent',
    group:       'System',
    bg:          'bg-red-600',
    text:        'text-white',
    icon:        '🚨',
    color:       '#dc2626',
    defaultText: 'Urgent: Immediate action may be required. Please read carefully.',
  },
  {
    id:          'success',
    label:       'Success / Notice',
    group:       'System',
    bg:          'bg-green-600',
    text:        'text-white',
    icon:        '✅',
    color:       '#16a34a',
    defaultText: 'Great news! Everything is running smoothly.',
  },
  {
    id:          'maintenance',
    label:       'Maintenance',
    group:       'System',
    bg:          'bg-slate-800',
    text:        'text-white',
    icon:        '🔧',
    color:       '#1a2744',
    defaultText: 'We are currently performing scheduled maintenance. Some features may be temporarily unavailable.',
  },

  // ── Promotional ───────────────────────────────────────────────────────────
  {
    id:          'promotional',
    label:       'Promotional',
    group:       'Promotional',
    bg:          'bg-purple-600',
    text:        'text-white',
    icon:        '🎉',
    color:       '#7c3aed',
    defaultText: '🎉 Special offer! Upgrade your plan today and get 20% off for life.',
  },
  {
    id:          'promotional/sale',
    label:       'Flash Sale',
    group:       'Promotional',
    bg:          'bg-pink-600',
    text:        'text-white',
    icon:        '🔥',
    color:       '#db2777',
    defaultText: '🔥 Flash Sale — Limited time only! Don\'t miss out on exclusive deals.',
  },
  {
    id:          'promotional/new-feature',
    label:       'New Feature',
    group:       'Promotional',
    bg:          'bg-indigo-600',
    text:        'text-white',
    icon:        '✨',
    color:       '#4f46e5',
    defaultText: '✨ Exciting new features are now live! Explore what\'s new on the platform.',
  },
  {
    id:          'promotional/referral',
    label:       'Referral Program',
    group:       'Promotional',
    bg:          'bg-teal-600',
    text:        'text-white',
    icon:        '🤝',
    color:       '#0d9488',
    defaultText: '🤝 Refer a friend and earn rewards! Share your unique link today.',
  },
  {
    id:          'promotional/anniversary',
    label:       'Anniversary',
    group:       'Promotional',
    bg:          'bg-yellow-500',
    text:        'text-white',
    icon:        '🎂',
    color:       '#eab308',
    defaultText: '🎂 We\'re celebrating our anniversary! Thank you for being part of our community.',
  },

  // ── Seasonal ──────────────────────────────────────────────────────────────
  {
    id:          'seasonal',
    label:       'Seasonal (Generic)',
    group:       'Seasonal',
    bg:          'bg-orange-500',
    text:        'text-white',
    icon:        '🌸',
    color:       '#f97316',
    defaultText: 'Wishing you and your loved ones a wonderful season.',
  },
  {
    id:          'seasonal/christmas',
    label:       'Christmas',
    group:       'Seasonal',
    bg:          'bg-red-700',
    text:        'text-white',
    icon:        '🎄',
    color:       '#b91c1c',
    defaultText: '🎄 Merry Christmas from all of us! Wishing you peace, joy, and comfort this holiday season.',
  },
  {
    id:          'seasonal/new-year',
    label:       'New Year',
    group:       'Seasonal',
    bg:          'bg-violet-700',
    text:        'text-white',
    icon:        '🎆',
    color:       '#6d28d9',
    defaultText: '🎆 Happy New Year! We wish you a year filled with cherished memories.',
  },
  {
    id:          'seasonal/easter',
    label:       'Easter',
    group:       'Seasonal',
    bg:          'bg-emerald-500',
    text:        'text-white',
    icon:        '🐣',
    color:       '#10b981',
    defaultText: '🐣 Happy Easter! May this season bring hope and renewal to you and your family.',
  },
  {
    id:          'seasonal/lent',
    label:       'Lent',
    group:       'Seasonal',
    bg:          'bg-purple-800',
    text:        'text-white',
    icon:        '🕊️',
    color:       '#5b21b6',
    defaultText: '🕊️ As we journey through Lent, may this season bring reflection, peace, and grace.',
  },
  {
    id:          'seasonal/thanksgiving',
    label:       'Thanksgiving',
    group:       'Seasonal',
    bg:          'bg-amber-700',
    text:        'text-white',
    icon:        '🍂',
    color:       '#b45309',
    defaultText: '🍂 Happy Thanksgiving! We are grateful for every member of our community.',
  },
  {
    id:          'seasonal/halloween',
    label:       'Halloween',
    group:       'Seasonal',
    bg:          'bg-orange-600',
    text:        'text-white',
    icon:        '🎃',
    color:       '#ea580c',
    defaultText: '🎃 Happy Halloween! Remember your loved ones with warmth this spooky season.',
  },
  {
    id:          'seasonal/mothers-day',
    label:       "Mother's Day",
    group:       'Seasonal',
    bg:          'bg-rose-500',
    text:        'text-white',
    icon:        '🌷',
    color:       '#f43f5e',
    defaultText: "🌷 Happy Mother's Day! Honour the mothers and maternal figures in your life.",
  },
  {
    id:          'seasonal/fathers-day',
    label:       "Father's Day",
    group:       'Seasonal',
    bg:          'bg-blue-700',
    text:        'text-white',
    icon:        '👔',
    color:       '#1d4ed8',
    defaultText: "👔 Happy Father's Day! Celebrating the fathers and father figures who shaped us.",
  },
  {
    id:          'seasonal/memorial-day',
    label:       'Memorial Day',
    group:       'Seasonal',
    bg:          'bg-slate-700',
    text:        'text-white',
    icon:        '🎖️',
    color:       '#374151',
    defaultText: '🎖️ We honour and remember those who gave everything. Thank you for your service.',
  },
  {
    id:          'seasonal/day-of-dead',
    label:       'Day of the Dead',
    group:       'Seasonal',
    bg:          'bg-fuchsia-700',
    text:        'text-white',
    icon:        '💐',
    color:       '#a21caf',
    defaultText: '💐 Día de los Muertos — Celebrating the lives of those we hold in our hearts forever.',
  },
  {
    id:          'seasonal/diwali',
    label:       'Diwali',
    group:       'Seasonal',
    bg:          'bg-yellow-600',
    text:        'text-white',
    icon:        '🪔',
    color:       '#ca8a04',
    defaultText: '🪔 Happy Diwali! May the festival of lights bring joy and blessings to all.',
  },

  // ── Custom ────────────────────────────────────────────────────────────────
  {
    id:          'custom',
    label:       'Custom Color',
    group:       'Custom',
    bg:          '',
    text:        'text-white',
    icon:        '📢',
    color:       null,   // falls back to bannerColor field
    defaultText: 'Enter your custom banner message here.',
  },
];

// ── Derived helpers ───────────────────────────────────────────────────────────

/** All unique group names in declaration order */
export const BANNER_THEME_GROUPS = [...new Set(BANNER_THEMES.map(t => t.group))];

/** Look up a theme object by id; falls back to the 'warning' theme */
export function getBannerTheme(id) {
  return BANNER_THEMES.find(t => t.id === id) ?? BANNER_THEMES.find(t => t.id === 'warning');
}

/** Resolve the effective background colour for a given config object */
export function resolveBannerColor(config) {
  const theme = getBannerTheme(config.bannerTheme);
  if (theme?.id === 'custom' || !theme?.color) return config.bannerColor || '#1a2744';
  return theme.color;
}

/** Returns the themes belonging to a specific group */
export function getThemesByGroup(group) {
  return BANNER_THEMES.filter(t => t.group === group);
}

// ── Persistence helpers ───────────────────────────────────────────────────────
export function loadBannerConfig() {
  try {
    return { ...BANNER_DEFAULTS, ...JSON.parse(localStorage.getItem(BANNER_STORAGE_KEY) || '{}') };
  } catch {
    return { ...BANNER_DEFAULTS };
  }
}

export function saveBannerConfig(config) {
  localStorage.setItem(BANNER_STORAGE_KEY, JSON.stringify(config));
  window.dispatchEvent(new CustomEvent('platform_banner_updated', { detail: config }));
}

/** Returns true if the banner should currently be visible based on scheduling */
export function isBannerScheduleActive(config) {
  if (!config.platform_maintenance) return false;
  const now = Date.now();
  if (config.startAt && new Date(config.startAt).getTime() > now) return false;
  if (config.endAt   && new Date(config.endAt).getTime()   < now) return false;
  return true;
}

// ── React Hook ────────────────────────────────────────────────────────────────
export function usePlatformBanner() {
  const [config, setConfig] = useState(loadBannerConfig);

  const refresh = useCallback(() => setConfig(loadBannerConfig()), []);

  useEffect(() => {
    window.addEventListener('platform_banner_updated', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('platform_banner_updated', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [refresh]);

  const update = useCallback((patch) => {
    const next = { ...loadBannerConfig(), ...patch };
    saveBannerConfig(next);
    setConfig(next);
  }, []);

  const isActive = isBannerScheduleActive(config);

  /** Resolved colour ready for inline style usage */
  const resolvedColor = resolveBannerColor(config);

  /** Full theme object for the current config */
  const currentTheme = getBannerTheme(config.bannerTheme);

  return { config, update, isActive, resolvedColor, currentTheme };
}